import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { api, formatApiError, waLink } from "@/lib/api";
import {
  CheckCircle2, Copy, FileSearch, HeartPulse, Loader2, Mail, MapPin, MessageCircle, Send,
} from "lucide-react";
import { toast } from "sonner";

const FASCE = ["Fino a 10.000 €", "10.000 – 30.000 €", "30.000 – 100.000 €", "Oltre 100.000 €", "Non lo so"];

const EMPTY = {
  nome: "", cognome: "", email: "", telefono: "", citta: "",
  fascia_debito: "", descrizione: "", disagio_psicologico: false,
};

const STATUS_INFO = {
  Nuova: "Richiesta ricevuta correttamente. Un operatore la prenderà in carico al più presto.",
  "In valutazione": "Stiamo analizzando la tua situazione. Ti ricontatteremo presto.",
  Contattata: "Ti abbiamo contattato: controlla la tua email o il telefono.",
  "Supporto psicologico attivo": "Per il tuo caso è attivo il percorso di supporto psicologico gratuito: ti comunicheremo giorno e orario in cui verrai chiamato dalla Dott.ssa V. Graffeo, la nostra psicologa.",
  Risolta: "La tua pratica è stata conclusa. Se hai bisogno di altro, siamo sempre qui.",
};

function RichiestaForm() {
  const [form, setForm] = useState(EMPTY);
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!privacy) {
      setError("Per inviare la richiesta devi accettare l'informativa privacy.");
      return;
    }
    if (!form.fascia_debito) {
      setError("Indica la fascia di debito: ci aiuta a capire la tua situazione.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/requests", form);
      setResult(data);
    } catch (err) {
      setError(formatApiError(err));
    }
    setLoading(false);
  };

  if (result) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-3xl bg-brand-mist border border-brand-aqua/30 p-8 sm:p-10 text-center"
        data-testid="richiesta-success"
      >
        <CheckCircle2 className="w-16 h-16 text-brand-aqua mx-auto" />
        <h2 className="mt-5 text-2xl font-extrabold text-brand-ink">Richiesta inviata!</h2>
        <p className="mt-3 text-slate-600">{result.message}</p>
        <div className="mt-6 inline-flex items-center gap-3 rounded-2xl bg-white border-2 border-brand-blue/20 px-6 py-4">
          <span className="text-sm font-bold text-slate-500 uppercase tracking-wider">Codice pratica</span>
          <span className="text-2xl font-extrabold text-brand-blue tracking-wide" data-testid="tracking-code-display">
            {result.tracking_code}
          </span>
          <button
            onClick={() => { navigator.clipboard.writeText(result.tracking_code); toast.success("Codice copiato"); }}
            className="p-2 rounded-lg hover:bg-brand-mist transition-colors"
            aria-label="Copia codice pratica"
            data-testid="tracking-code-copy-button"
          >
            <Copy className="w-5 h-5 text-brand-blue" />
          </button>
        </div>
        <p className="mt-4 text-sm text-slate-500">
          Annota o fotografa questo codice: ti servirà per verificare lo stato della pratica.
        </p>
        <a
          href={waLink(`Buongiorno, ho appena inviato la richiesta ${result.tracking_code}. Vorrei parlarne con un operatore.`)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white font-bold px-6 h-12"
          data-testid="richiesta-success-whatsapp"
        >
          <MessageCircle className="w-5 h-5" />
          Nel frattempo, scrivici su WhatsApp
        </a>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl border border-slate-100 bg-white shadow-xl shadow-brand-blue/5 p-8 sm:p-10" data-testid="richiesta-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="rq-nome" className="label-field">Nome *</label>
          <input id="rq-nome" required value={form.nome} onChange={set("nome")} className="input-field" data-testid="richiesta-nome-input" />
        </div>
        <div>
          <label htmlFor="rq-cognome" className="label-field">Cognome *</label>
          <input id="rq-cognome" required value={form.cognome} onChange={set("cognome")} className="input-field" data-testid="richiesta-cognome-input" />
        </div>
        <div>
          <label htmlFor="rq-email" className="label-field">Email *</label>
          <input id="rq-email" type="email" required value={form.email} onChange={set("email")} className="input-field" data-testid="richiesta-email-input" />
        </div>
        <div>
          <label htmlFor="rq-telefono" className="label-field">Telefono <span className="font-normal text-slate-400">(facoltativo)</span></label>
          <input id="rq-telefono" type="tel" value={form.telefono} onChange={set("telefono")} className="input-field" data-testid="richiesta-telefono-input" />
        </div>
        <div>
          <label htmlFor="rq-citta" className="label-field">Città *</label>
          <input id="rq-citta" required value={form.citta} onChange={set("citta")} className="input-field" data-testid="richiesta-citta-input" />
        </div>
        <div>
          <label htmlFor="rq-fascia" className="label-field">Ammontare indicativo dei debiti *</label>
          <select id="rq-fascia" value={form.fascia_debito} onChange={set("fascia_debito")} className="input-field" data-testid="richiesta-fascia-select">
            <option value="">Seleziona…</option>
            {FASCE.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="rq-descrizione" className="label-field">Raccontaci la tua situazione *</label>
        <textarea
          id="rq-descrizione"
          required
          rows={5}
          value={form.descrizione}
          onChange={set("descrizione")}
          className="input-field h-auto py-4 resize-y"
          placeholder="Scrivi con parole tue: non serve alcun termine tecnico."
          data-testid="richiesta-descrizione-textarea"
        />
      </div>

      <label className="mt-6 flex items-start gap-3 cursor-pointer rounded-2xl border-2 border-[#c24e2c]/25 bg-[#fbeae5]/60 p-5" data-testid="richiesta-disagio-label">
        <input
          type="checkbox"
          checked={form.disagio_psicologico}
          onChange={(e) => setForm({ ...form, disagio_psicologico: e.target.checked })}
          className="mt-1.5 w-5 h-5 accent-[#c24e2c]"
          data-testid="richiesta-disagio-checkbox"
        />
        <span className="text-brand-ink">
          <span className="flex items-center gap-2 font-bold text-[#963b21]">
            <HeartPulse className="w-5 h-5" />
            Sto vivendo un forte disagio psicologico
          </span>
          <span className="mt-1 block text-slate-600">
            Il supporto psicologico gratuito — grazie alla collaborazione della Dott.ssa V.
            Graffeo, psicologa professionista — è attualmente disponibile per i casi di maggiore
            necessità. L'associazione valuterà la tua richiesta e ti ricontatterà comunicandoti
            giorno e orario in cui verrai chiamato dalla psicologa.
          </span>
        </span>
      </label>

      <label className="mt-5 flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={privacy}
          onChange={(e) => setPrivacy(e.target.checked)}
          className="mt-1.5 w-5 h-5 accent-brand-blue"
          data-testid="richiesta-privacy-checkbox"
        />
        <span className="text-slate-600">
          Ho letto{" "}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-blue font-semibold underline underline-offset-2" data-testid="richiesta-privacy-link">
            l'informativa privacy
          </a>{" "}
          e acconsento al trattamento dei miei dati. *
        </span>
      </label>

      {error && (
        <p className="mt-4 rounded-xl bg-red-50 text-red-700 font-semibold px-4 py-3" role="alert" data-testid="richiesta-error">
          {error}
        </p>
      )}

      <button type="submit" disabled={loading} className="btn-primary w-full mt-7 disabled:opacity-60" data-testid="richiesta-submit-button">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        {loading ? "Invio in corso…" : "Invia la richiesta gratuita"}
      </button>
    </form>
  );
}

function StatoPratica() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const check = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    setLoading(true);
    try {
      const { data } = await api.get(`/requests/status/${encodeURIComponent(code.trim())}`);
      setResult(data);
    } catch (err) {
      setError(formatApiError(err));
    }
    setLoading(false);
  };

  return (
    <div className="rounded-3xl border border-slate-100 bg-white shadow-xl shadow-brand-blue/5 p-8 sm:p-10" data-testid="stato-pratica-panel">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-11 h-11 rounded-2xl bg-brand-aqua text-white flex items-center justify-center">
          <FileSearch className="w-5 h-5" />
        </span>
        <h2 className="text-2xl font-extrabold text-brand-ink">Verifica lo stato della pratica</h2>
      </div>
      <p className="text-slate-600 mb-6">
        Inserisci il codice che hai ricevuto al momento della richiesta (es. AVD-2026-1234).
      </p>
      <form onSubmit={check} className="flex flex-col sm:flex-row gap-3">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          required
          placeholder="AVD-2026-0000"
          className="input-field flex-1 uppercase tracking-wider font-bold"
          data-testid="tracking-code-input"
        />
        <button type="submit" disabled={loading} className="btn-primary shrink-0 disabled:opacity-60" data-testid="tracking-code-check-button">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileSearch className="w-5 h-5" />}
          Verifica
        </button>
      </form>

      {error && (
        <p className="mt-5 rounded-xl bg-red-50 text-red-700 font-semibold px-4 py-3" role="alert" data-testid="tracking-error">
          {error}
        </p>
      )}

      {result && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 rounded-2xl border-2 border-brand-aqua/30 bg-brand-mist p-6"
          data-testid="tracking-result"
        >
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-bold text-slate-500 uppercase tracking-wider">Pratica</span>
            <span className="font-extrabold text-brand-ink text-lg">{result.tracking_code}</span>
            <span className="ml-auto rounded-full bg-brand-blue text-white text-sm font-bold px-4 py-1.5" data-testid="tracking-status-badge">
              {result.status}
            </span>
          </div>
          {result.urgenza === "Critico" && (
            <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#fbeae5] text-[#963b21] text-sm font-bold px-4 py-1.5">
              <HeartPulse className="w-4 h-4" /> Priorità massima — supporto psicologico
            </p>
          )}
          <p className="mt-4 text-slate-600 leading-relaxed" data-testid="tracking-status-message">
            {STATUS_INFO[result.status] || "La tua pratica è in lavorazione."}
          </p>
        </motion.div>
      )}
    </div>
  );
}

export default function Assistenza() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "stato" ? "stato" : "richiesta";

  return (
    <div className="container-site py-16 lg:py-24" data-testid="assistenza-page">
      <Reveal className="max-w-2xl mx-auto text-center">
        <span className="eyebrow justify-center">Assistenza gratuita</span>
        <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight text-brand-ink">
          Raccontaci la tua situazione.
          <span className="text-brand-blue"> Ti risponderemo noi.</span>
        </h1>
        <p className="mt-6 text-lg text-slate-600 leading-relaxed">
          Compila il modulo: riceverai subito un codice pratica per seguire il tuo percorso.
          Tutto è gratuito e riservato. Se preferisci, puoi scriverci direttamente su WhatsApp.
        </p>
      </Reveal>

      <Reveal delay={0.05} className="max-w-3xl mx-auto mt-10">
        <div className="rounded-3xl overflow-hidden border-4 border-white shadow-xl shadow-brand-blue/10" data-testid="assistenza-image">
          <img
            src="https://images.unsplash.com/photo-1495653797063-114787b77b23?auto=format&fit=crop&w=1200&q=80"
            alt="Mani che si sostengono a vicenda in un gesto di solidarietà"
            loading="lazy"
            className="w-full h-56 object-cover"
          />
        </div>
      </Reveal>

      <Reveal delay={0.1} className="max-w-3xl mx-auto mt-10">
        <div className="grid grid-cols-2 gap-2 rounded-full bg-brand-mist p-2" role="tablist" data-testid="assistenza-tabs">
          <button
            role="tab"
            aria-selected={tab === "richiesta"}
            onClick={() => setParams({})}
            data-testid="assistenza-tab-richiesta"
            className={`rounded-full h-12 font-bold transition-colors ${
              tab === "richiesta" ? "bg-brand-blue text-white shadow" : "text-brand-ink hover:bg-white"
            }`}
          >
            Richiedi assistenza
          </button>
          <button
            role="tab"
            aria-selected={tab === "stato"}
            onClick={() => setParams({ tab: "stato" })}
            data-testid="assistenza-tab-stato"
            className={`rounded-full h-12 font-bold transition-colors ${
              tab === "stato" ? "bg-brand-blue text-white shadow" : "text-brand-ink hover:bg-white"
            }`}
          >
            Verifica stato pratica
          </button>
        </div>

        <div className="mt-8">{tab === "richiesta" ? <RichiestaForm /> : <StatoPratica />}</div>
      </Reveal>

      <Reveal delay={0.15} className="max-w-3xl mx-auto mt-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="assistenza-contatti">
          <a href="mailto:info@associazionevittimedeldebito.it" className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm hover:shadow-md transition-shadow" data-testid="contatti-email-card">
            <Mail className="w-6 h-6 text-brand-blue shrink-0" />
            <span className="font-semibold text-brand-ink text-sm break-all">info@associazionevittimedeldebito.it</span>
          </a>
          <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="contatti-sedi-card">
            <MapPin className="w-6 h-6 text-brand-blue shrink-0" />
            <span className="font-semibold text-brand-ink text-sm">Milano, Via Pordenone 13 · Gallarate, Via Scipione Ronchetti 8</span>
          </div>
          <a
            href={waLink("Buongiorno, vorrei chiedere assistenza gratuita all'Associazione Vittime del Debito.")}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-2xl bg-[#25D366] text-white p-5 shadow-sm hover:shadow-md transition-shadow"
            data-testid="contatti-whatsapp-card"
          >
            <MessageCircle className="w-6 h-6 shrink-0" />
            <span className="font-bold text-sm">Scrivici su WhatsApp</span>
          </a>
        </div>
      </Reveal>
    </div>
  );
}
